import type {
  BounceTaxonomy,
  DeferredPatterns,
  DeliveryHealth,
  LatencyPercentiles,
  ProviderErrorRow,
  ProviderUptimeRow,
  RetryRecoveryStats,
} from '@/features/analytics/analyticsApi';
import { useListProvidersQuery } from '@/features/providers/providersApi';
import { formatNumber, formatPercent } from '@/lib/i18n';

/**
 * Cards for the Delivery Health tab. Each card surfaces one piece of the
 * payload returned by `GET /api/analytics/delivery-health`. Kept in one file
 * since the cards are visually small and tightly related — splitting them
 * into 5 files would add navigation overhead without much benefit.
 */

const CARD_BASE: React.CSSProperties = {
  background: 'var(--paper)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-3)',
  padding: 18,
};

const CARD_LABEL: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.06,
  color: 'var(--ink-3)',
};

export function LatencyPercentilesCard({ latency }: { latency: LatencyPercentiles }) {
  const cells = [
    { label: 'p50', value: latency.p50, hint: 'Median delivery time. Should be < 1s.' },
    { label: 'p95', value: latency.p95, hint: 'Tail. > 5s suggests provider degradation.' },
    { label: 'p99', value: latency.p99, hint: 'Edge tail. Spikes here are usually a single bad batch.' },
  ];
  return (
    <div className="mf-card" style={CARD_BASE}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span className="mono" style={CARD_LABEL}>Latency percentiles · 30d</span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
          n = {formatNumber(latency.sampleSize)}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {cells.map((c) => (
          <div key={c.label} title={c.hint}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{c.label}</div>
            <div className="display" style={{ fontSize: 28, fontWeight: 500, color: 'var(--ink)', marginTop: 4 }}>
              {c.value != null ? `${formatNumber(c.value)}ms` : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProviderErrorBreakdownCard({ rows }: { rows: ProviderErrorRow[] }) {
  const { data: providers = [] } = useListProvidersQuery();
  const labelOf = (id: string) => {
    const p = providers.find((x) => x.id === id);
    return p ? `${p.providerType} · ${p.displayName}` : id.slice(0, 8);
  };
  if (rows.length === 0) {
    return (
      <div className="mf-card" style={CARD_BASE}>
        <div className="mono" style={{ ...CARD_LABEL, marginBottom: 14 }}>Provider errors · 30d</div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No errors in the window. Send some traffic — anomalies surface here.
        </div>
      </div>
    );
  }
  return (
    <div className="mf-card" style={CARD_BASE}>
      <div className="mono" style={{ ...CARD_LABEL, marginBottom: 14 }}>Provider errors · 30d</div>
      <table className="mf-table" style={{ background: 'transparent', fontSize: 12.5 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Provider</th>
            <th style={{ textAlign: 'right' }}>Bounced</th>
            <th style={{ textAlign: 'right' }}>Complained</th>
            <th style={{ textAlign: 'right' }}>Failed</th>
            <th style={{ textAlign: 'right' }}>Deferred</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.providerConnectionId}>
              <td>{labelOf(r.providerConnectionId)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.bounced)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.complained)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.failed)}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(r.deferred)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BounceTaxonomyChart({ buckets }: { buckets: BounceTaxonomy }) {
  const total = Object.values(buckets).reduce((a, b) => a + b, 0);
  const segments = [
    { key: 'hard', color: 'var(--err)', label: 'Hard' },
    { key: 'soft', color: 'var(--warn, #d4a52d)', label: 'Soft' },
    { key: 'spam_complaint', color: 'var(--flame-2)', label: 'Spam complaint' },
    { key: 'blocklist', color: '#8b1e3f', label: 'Blocklist' },
    { key: 'unknown', color: 'var(--ink-3)', label: 'Unknown' },
  ] as const;
  return (
    <div className="mf-card" style={CARD_BASE}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span className="mono" style={CARD_LABEL}>Bounce taxonomy · 30d</span>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
          {formatNumber(total)} total
        </span>
      </div>
      {total === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No bounces yet — nice deliverability.</div>
      ) : (
        <>
          <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
            {segments.map((s) => {
              const v = buckets[s.key];
              if (v === 0) return null;
              return (
                <div
                  key={s.key}
                  title={`${s.label}: ${formatNumber(v)} (${formatPercent(v / total)})`}
                  style={{ flex: v, background: s.color, minWidth: 0 }}
                />
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, fontSize: 11.5 }}>
            {segments.map((s) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, background: s.color, borderRadius: 2 }} />
                <span style={{ flex: 1, color: 'var(--ink-2)' }}>{s.label}</span>
                <span className="mono" style={{ color: 'var(--ink-3)' }}>
                  {formatNumber(buckets[s.key])}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function RetryRecoveryGauge({ stats }: { stats: RetryRecoveryStats }) {
  const value = stats.recoveryRate;
  return (
    <div className="mf-card" style={CARD_BASE}>
      <div className="mono" style={{ ...CARD_LABEL, marginBottom: 14 }}>Retry recovery · 30d</div>
      <div className="display" style={{ fontSize: 36, fontWeight: 500, color: value != null ? 'var(--spark, #2c8a55)' : 'var(--ink-3)' }}>
        {value != null ? formatPercent(value, 0) : '—'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 8 }}>
        {value != null ? (
          <>
            {formatNumber(stats.recovered)} of {formatNumber(stats.firstAttemptFailures)} first-attempt
            failures eventually delivered. Higher = better resilience.
          </>
        ) : (
          'No first-attempt failures in the window.'
        )}
      </div>
    </div>
  );
}

export function DeferredPatternsCard({ deferred }: { deferred: DeferredPatterns }) {
  return (
    <div className="mf-card" style={CARD_BASE}>
      <div className="mono" style={{ ...CARD_LABEL, marginBottom: 14 }}>Deferred sends</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>currently deferred</div>
          <div className="display" style={{ fontSize: 28, fontWeight: 500, color: 'var(--ink)', marginTop: 4 }}>
            {formatNumber(deferred.currentlyDeferred)}
          </div>
        </div>
        <div>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>avg age</div>
          <div className="display" style={{ fontSize: 28, fontWeight: 500, color: 'var(--ink)', marginTop: 4 }}>
            {deferred.avgAgeSeconds != null ? `${formatNumber(Math.round(deferred.avgAgeSeconds / 60))}m` : '—'}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 12 }}>
        Growing counts here usually mean ISP throttling or sender-reputation issues.
      </div>
    </div>
  );
}

export function ProviderUptimeCard({ rows }: { rows: ProviderUptimeRow[] }) {
  const { data: providers = [] } = useListProvidersQuery();
  const labelOf = (id: string) => {
    const p = providers.find((x) => x.id === id);
    return p ? `${p.providerType} · ${p.displayName}` : id.slice(0, 8);
  };
  if (rows.length === 0) {
    return (
      <div className="mf-card" style={CARD_BASE}>
        <div className="mono" style={{ ...CARD_LABEL, marginBottom: 14 }}>Provider uptime · 30d</div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No active hours yet. Send through a provider to populate this card.
        </div>
      </div>
    );
  }
  return (
    <div className="mf-card" style={CARD_BASE}>
      <div className="mono" style={{ ...CARD_LABEL, marginBottom: 14 }}>Provider uptime · 30d</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r) => {
          const value = r.uptime;
          const tone = value == null ? 'idle' : value >= 0.99 ? 'good' : value >= 0.95 ? 'warn' : 'bad';
          return (
            <div key={r.providerConnectionId} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, fontSize: 12.5 }}>
                <div style={{ fontWeight: 500 }}>{labelOf(r.providerConnectionId)}</div>
                <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                  {r.activeHours} active · {r.degradedHours} degraded
                </div>
              </div>
              <div
                className="display"
                style={{
                  fontSize: 22,
                  fontWeight: 500,
                  color:
                    tone === 'good'
                      ? 'var(--spark, #2c8a55)'
                      : tone === 'warn'
                        ? 'var(--warn, #b48323)'
                        : tone === 'bad'
                          ? 'var(--err)'
                          : 'var(--ink-3)',
                }}
              >
                {value != null ? formatPercent(value, 2) : '—'}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.5, marginTop: 12 }}>
        An hour counts as degraded if more than 50% of its sends failed. Idle hours don't penalize the score.
      </div>
    </div>
  );
}

export function DeliveryHealthGrid({ data }: { data: DeliveryHealth }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <LatencyPercentilesCard latency={data.latency} />
      <RetryRecoveryGauge stats={data.retryRecovery} />
      <BounceTaxonomyChart buckets={data.bounceTaxonomy} />
      <DeferredPatternsCard deferred={data.deferred} />
      <div style={{ gridColumn: '1 / -1' }}>
        <ProviderUptimeCard rows={data.uptime} />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <ProviderErrorBreakdownCard rows={data.providerErrors} />
      </div>
    </div>
  );
}
